<template>
    <rkm-form
        :show-legend="!data.model.exportado"
        ref="Form"
    >
        <rkm-row>
            <rkm-col cols="5">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !!model.id"
                    :repository="repositories.ocupacao"
                    :search-fields="fields.autocomplete.ocupacao"
                    :text-field="o => `${o.codigo} - ${o.nome}`"
                    :validations="validations.ocupacao"
                    label="CBO"
                    v-model="model.ocupacao"
                />
            </rkm-col>

            <rkm-col cols="5">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !!model.id || !model.ocupacao"
                    :repository="repositories.procedimentoSigtap"
                    :request-query-string-params="{
                        instrumento_registro: enums.registro.APAC_PROCEDIMENTO_SECUNDARIO,
                        procedimento_principal_codigo: data.model.procedimentoPrincipal.codigo,
                        competencia_id: data.model.competenciaId,
                        ocupacao_id: getSafely(() => model.ocupacao.id),
                        paciente_id: data.model.paciente.id,
                        with_atributos_complementares: true
                    }"
                    :search-fields="fields.autocomplete.procedimento"
                    :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                    :validations="validations.procedimento"
                    label="Procedimento"
                    v-model="model.procedimento"
                />
            </rkm-col>

            <rkm-col cols="2">
                <rkm-form-input
                    :disabled="data.model.exportado || !model.procedimento"
                    :validations="validations.quantidade"
                    label="Quantidade"
                    number
                    v-model="model.quantidade"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.procedimento"
                    :repository="repositories.cid"
                    :request-query-string-params="{
                        procedimento_id: getSafely(() => model.procedimento.id),
                        principal: true,
                    }"
                    :search-fields="fields.autocomplete.cidPrincipal"
                    :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                    :validations="validations.cidPrincipal"
                    label="CID principal"
                    v-model="model.cidPrincipal"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.procedimento"
                    :repository="repositories.cid"
                    :request-query-string-params="{
                        procedimento_id: getSafely(() => model.procedimento.id),
                        principal: false,
                    }"
                    :search-fields="fields.autocomplete.cidSecundario"
                    :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                    :validations="validations.cidSecundario"
                    label="CID secundário"
                    v-model="model.cidSecundario"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.procedimento"
                    :repository="repositories.servico"
                    :request-query-string-params="{
                        procedimento_id: getSafely(() => model.procedimento.id)
                    }"
                    :search-fields="fields.autocomplete.servico"
                    :validations="validations.servico"
                    label="Serviço"
                    text-field="nome"
                    v-model="model.servico"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="data.model.exportado || !model.servico"
                    :repository="repositories.classificacao"
                    :request-query-string-params="{
                        procedimento_id: getSafely(() => model.procedimento.id),
                        servico_id: getSafely(() => model.servico.id),
                    }"
                    :search-fields="fields.autocomplete.classificacao"
                    :validations="validations.classificacao"
                    label="Classificação"
                    text-field="nome"
                    v-model="model.classificacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <rkm-form-input
                    :disabled="data.model.exportado"
                    :validations="validations.cnpjCessaoCredito"
                    label="CNPJ cessão de crédito"
                    v-mask="mask.cnpj"
                    v-model="model.cnpjCessaoCredito"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-input
                    :disabled="data.model.exportado"
                    :validations="validations.numeroNotaFiscalCessao"
                    label="Número da nota fiscal de cessão"
                    number
                    v-model="model.numeroNotaFiscalCessao"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-input
                    :disabled="data.model.exportado"
                    :validations="validations.unidadePrestadoraTerceiroCnes"
                    label="CNES da unidade prestadora de terceiro"
                    number
                    v-model="model.unidadePrestadoraTerceiroCnes"
                />
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Mixins.
import { repository, object, formats, mask } from '@/mixins'

// Enums.
import registro from '@/enums/sigtap/registro'

export default {
    computed: {
        fields() {

            return {
                autocomplete: {
                    ocupacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    procedimento: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidPrincipal: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidSecundario: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    servico: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    classificacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                }
            }
        },
        validations() {

            return {
                ocupacao: { required: true },
                procedimento: { required: true },
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.model.procedimento ? this.model.procedimento.quantidadeMaximaExecucao : null,
                },
                cidPrincipal: {},
                cidSecundario: {},
                servico: {},
                classificacao: {},
                cnpjCessaoCredito: {
                    min_max_without_mask: 14,
                    cnpj: true,
                },
                numeroNotaFiscalCessao: { max: 6 },
                unidadePrestadoraTerceiroCnes: { min: 7, max: 7 },
            }
        },
    },
    created() {

        this.repositories = {
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            procedimentoSigtap: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
            servico: this.getConectaSusRepository('sigtap.servico'),
            classificacao: this.getConectaSusRepository('sigtap.classificacao'),
        }
    },
    data: () => ({
        model: {
            id: null,
            ocupacao: null,
            procedimento: null,
            quantidade: null,
            cidPrincipal: null,
            cidSecundario: null,
            servico: null,
            classificacao: null,
            cnpjCessaoCredito: null,
            numeroNotaFiscalCessao: null,
            unidadePrestadoraTerceiroCnes: null,
            saved: false,
        },
        enums: { registro },
    }),
    methods: {
        load(item = null) {

            if (item) {

                this.model = this.clone(item)
            }

            this.addWatchers()
        },
        addWatchers() {

            this.$watch('model.ocupacao', () => {

                this.model.procedimento = null
            })

            this.$watch('model.procedimento', () => {

                this.model.quantidade = null
                this.model.cidPrincipal = null
                this.model.cidSecundario = null
                this.model.servico = null
            })

            this.$watch('model.servico', () => {

                this.model.classificacao = null
            })
        },
        add() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    const model = this.clone(this.model)

                    if (model.id) {

                        this.$set(
                            this.data.model.procedimentosSecundarios,
                            this.data.model.procedimentosSecundarios.findIndex(p => p.id === model.id),
                            model
                        )
                    }
                    else {

                        model.id = (new Date()).getTime()

                        this.data.model.procedimentosSecundarios.push(model)
                    }

                    this.$emit('save')
                })
        },
    },
    mixins: [repository, object, formats, mask],
    props: {
        data: { type: Object, required: true },
    },
}
</script>